// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import AccountBoxIcon from "@material-ui/icons/AccountBox";
import BusinessIcon from "@material-ui/icons/Business";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import {
  mdiCalculator,
  mdiCog,
  mdiKey,
  mdiLayers,
  mdiMapCheck,
  mdiMemory,
  mdiTrendingUp
} from "@mdi/js";
import { IS_INTERNAL_ENV, IS_SSO_BRIDGE } from "constants/app.constants";
import { RootState } from "store/rootReducer";
import { selectActiveSetting, usePanelStore } from "stores/panelStore";
import styled from "styled-components";

import { useUser } from "hooks";
import useCanUseGeoModels from "hooks/useCanUseGeoModels";

import { BaseButton, SubHeading } from "components/base";
import AccountSecurity from "components/icons/AccountSecurity";
import DataSource from "components/icons/DataSource";
import DisplaySettings from "components/icons/DisplaySettingsIcon";

import useBetaFeatures from "../../hooks/useBetaFeatures";
import "./OrganizationSettingsSideNav.scss";

const useStyles = makeStyles({
  navItems: {
    fontSize: "1.2em",
    textAlign: "left",
    display: "inline"
  }
});

export default function OrganizationSettingsSideNav() {
  const role = useSelector((state: RootState) => state.auth.user.role);
  const { isAtLeastAdmin, isSystemAdmin } = useUser();
  // This sidebar mounts outside the layout's isAuthenticated guard, so it
  // renders whatever the viewer's role — don't fetch the org access list for
  // anyone who isn't an admin.
  const canUseGeoModels = useCanUseGeoModels(!!isAtLeastAdmin);
  const classes = useStyles();
  const { hasFeature } = useBetaFeatures();
  const activeSetting = usePanelStore(selectActiveSetting);
  const { closePanel, setActiveSetting } = usePanelStore();
  const showSystemShapefileManagement = isSystemAdmin && IS_INTERNAL_ENV;
  return (
    role === "Admin" && (
      <div className="OrganizationSettingSideNav">
        <ul className="settings-nav">
          <li className="back-dashboard">
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => closePanel()}>
              <div>
                <KeyboardBackspaceIcon style={{ fontSize: "24px" }} />
              </div>
              Back to dashboard
            </BaseButton>
          </li>
          <div className="settings-section">
            <SubHeading>General</SubHeading>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationProfile" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationProfile")}>
                <NavItem>
                  <IconWrapper>
                    <BusinessIcon style={{ fontSize: "24px" }}></BusinessIcon>
                  </IconWrapper>
                  <Title>Company Profile</Title>
                </NavItem>
              </BaseButton>
            </li>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationSecurity" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationSecurity")}>
                <NavItem className="inline-flex">
                  <IconWrapper>
                    <AccountSecurity></AccountSecurity>
                  </IconWrapper>
                  <Title>Account & Security</Title>
                </NavItem>
              </BaseButton>
            </li>
          </div>
          {/* Novi Auth bridge: user management lives at the edge. "Users" is the
              only item in this section, so the section goes with it. */}
          {!IS_SSO_BRIDGE && (
            <div className="settings-section">
              <SubHeading>Subscription</SubHeading>
              <li
                className={`settings-nav-item ${
                  activeSetting === "OrganizationMembers" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  onClick={() => setActiveSetting("OrganizationMembers")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      {" "}
                      <AccountBoxIcon style={{ fontSize: "24px" }}></AccountBoxIcon>
                    </IconWrapper>
                    <Title>Users</Title>
                  </NavItem>
                </BaseButton>
              </li>
            </div>
          )}
          <div className="settings-section">
            <SubHeading>Integrations</SubHeading>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationDataSources" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationDataSources")}>
                <NavItem className="inline-flex">
                  <IconWrapper>
                    <DataSource />
                  </IconWrapper>
                  <Title>Data Source</Title>
                </NavItem>
              </BaseButton>
            </li>

            {hasFeature("Developer Api Tokens") && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "DeveloperApiTokenManagement" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  onClick={() => setActiveSetting("DeveloperApiTokenManagement")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiKey} size={1.6} />
                    </IconWrapper>
                    <Title>Developer API Tokens</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
          </div>{" "}
          <div className="settings-section">
            <SubHeading>Configuration</SubHeading>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationForecasts" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationForecasts")}>
                <NavItem className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiTrendingUp} size={1.6} />
                  </IconWrapper>
                  <Title>Organization Forecasts</Title>
                </NavItem>
              </BaseButton>
            </li>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationShapefiles" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationShapefiles")}>
                <NavItem className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiLayers} size={1.6} />
                  </IconWrapper>
                  <Title>Org Shapefiles & GeoTIFFs</Title>
                </NavItem>
              </BaseButton>
            </li>
            {showSystemShapefileManagement && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "SystemShapefiles" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  onClick={() => setActiveSetting("SystemShapefiles")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiLayers} size={1.6} />
                    </IconWrapper>
                    <Title>System Shapefiles & GeoTIFFs</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
            {hasFeature("Org Geo Fields") && hasFeature("Cutoff Geo Models") && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "OrganizationGeoModelConfigurations" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  onClick={() => setActiveSetting("OrganizationGeoModelConfigurations")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiMapCheck} size={1.6} />
                    </IconWrapper>
                    <Title>Geo Model Configurations</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
            {hasFeature("Org Geo Fields") && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "OrganizationGeoCalculationJobs" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  data-testid={"geo-model-calculations-navigation"}
                  onClick={() => setActiveSetting("OrganizationGeoCalculationJobs")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiCalculator} size={1.6} />
                    </IconWrapper>
                    <Title>Geo Model Calculations</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
            {canUseGeoModels && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "GeoModelAccess" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  data-testid={"geo-model-access"}
                  onClick={() => setActiveSetting("GeoModelAccess")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiCalculator} size={1.6} />
                    </IconWrapper>
                    <Title>Geo Model Access</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
            {canUseGeoModels && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "GeoModelDefaults" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  data-testid={"geo-model-defaults"}
                  onClick={() => setActiveSetting("GeoModelDefaults")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiCog} size={1.6} />
                    </IconWrapper>
                    <Title>Geo Model Defaults</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
            {hasFeature("Org Geo Simulations") && (
              <li
                className={`settings-nav-item ${
                  activeSetting === "OrganizationGeoSimulationJobs" ? "active" : ""
                }`}>
                <BaseButton
                  appearance="subtle"
                  className={classes.navItems}
                  data-testid={"geo-model-simulations-navigation"}
                  onClick={() => setActiveSetting("OrganizationGeoSimulationJobs")}>
                  <NavItem className="inline-flex">
                    <IconWrapper>
                      <Icon path={mdiMemory} size={1.6} />
                    </IconWrapper>
                    <Title>Geo Model Simulations</Title>
                  </NavItem>
                </BaseButton>
              </li>
            )}
          </div>
          <div className="settings-section">
            <SubHeading>Preferences</SubHeading>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationMapDefaults" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                data-testid={"organization-map-defaults-navigation"}
                onClick={() => setActiveSetting("OrganizationMapDefaults")}>
                <NavItem className="inline-flex">
                  <IconWrapper>
                    <SettingsBackupRestoreIcon style={{ fontSize: "20px" }} />
                  </IconWrapper>
                  <Title>Defaults</Title>
                </NavItem>
              </BaseButton>
            </li>
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationDisplaySettings" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationDisplaySettings")}>
                <NavItem>
                  <IconWrapper>
                    <DisplaySettings />
                  </IconWrapper>
                  <Title> Display Settings</Title>
                </NavItem>
              </BaseButton>
            </li>
          </div>
        </ul>
      </div>
    )
  );
}

const Title = styled.div`
  height: 100%;
  display: flex;
`;

const IconWrapper = styled.div`
  min-width: 30px;
`;

const NavItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;
